import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, useEffect, type ReactNode } from 'react'
import { AuthProvider, useAuth } from './auth/AuthContext'

import { HomePage } from './pages/HomePage'

/**
 * Every route but Home is lazy. Home is the landing page for all six roles, so
 * loading it eagerly costs nothing and saves a spinner on the one screen
 * everybody sees first; the rest are per-role and mostly never visited by a
 * given user — `/settings` alone pulls in four tab editors and the whole colour
 * picker. This took the entry chunk from 877 kB to ~330 kB.
 *
 * `React.lazy` needs a default export, which none of these have (the repo
 * exports named components on purpose — a default export can be imported under
 * any name and typos stop being compile errors). The `.then` re-map is that
 * bridge, not an accident.
 */
const WorkPage = lazy(() => import('./pages/work/WorkPage').then((m) => ({ default: m.WorkPage })))
const ActivityPage = lazy(() =>
  import('./pages/ActivityPage').then((m) => ({ default: m.ActivityPage })),
)
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const DataPage = lazy(() => import('./pages/DataPage').then((m) => ({ default: m.DataPage })))
const Audit = lazy(() => import('./pages/Audit').then((m) => ({ default: m.Audit })))
const AdminUploadPage = lazy(() =>
  import('./pages/AdminUploadPage').then((m) => ({ default: m.AdminUploadPage })),
)
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const UserAnnotationsPage = lazy(() =>
  import('./pages/dashboard/UserAnnotationsPage').then((m) => ({
    default: m.UserAnnotationsPage,
  })),
)
// Lazy matters more here than elsewhere: this chunk carries @xyflow/react, which
// nobody but a maintainer editing a pipeline ever needs.
const WorkflowsPage = lazy(() =>
  import('./pages/WorkflowsPage').then((m) => ({ default: m.WorkflowsPage })),
)
const CollectionsPage = lazy(() =>
  import('./pages/collections/CollectionsPage').then((m) => ({ default: m.CollectionsPage })),
)
const CollectionDetailPage = lazy(() =>
  import('./pages/collections/CollectionDetailPage').then((m) => ({
    default: m.CollectionDetailPage,
  })),
)

import { AppShell } from './layout/AppShell'
import { getDefaultLanding, NAV } from './layout/SidebarNav'
import { PageContainer, PageHeader } from './layout/PageHeader'
import { Button } from './components/ui/button'
import type { Role } from './api/types'
import { Toaster } from './components/ui/toaster'
import { ThemeProvider } from './theme/ThemeProvider'
import { LoadingSpinner } from './components/LoadingSpinner'
import { ErrorToastBody } from './components/ErrorToastBody'
import { toast as shadcnToast } from './components/ui/use-toast'
import { refreshAfterMutation } from './lib/refresh'
import { redirectToLogin } from './lib/session'

/**
 * The shipped QueryClient. Exported so tests can build the real thing rather
 * than a bare `new QueryClient()` that wouldn't carry either cache handler.
 *
 * - `queryCache.onError` redirects to login on a 401 from any query (expired
 *   session) and surfaces a request id when one is attached. Must exist before
 *   QueryClientProvider mounts.
 * - `mutationCache.onSuccess` is the single post-mutation refresh for the whole
 *   app; see `lib/refresh.ts` for why it's blanket and why it repeats.
 */
export function makeQueryClient() {
  // Declared here so the mutation callback can close over the finished client.
  // No TDZ risk: the callback only runs on a mutation success, long after this
  // initializer has returned.
  const client: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      // Fire-and-forget on purpose. query-core awaits the cache-level
      // `onSuccess` *before* the mutation's own `onSuccess`
      // (`mutation.js` lines 116 and 123), so returning the invalidation
      // promise here would stall every success toast in the app behind a
      // refetch round-trip. Keep the `void`.
      onSuccess: () => {
        void refreshAfterMutation(client)
      },
      // A 401 on a button used to be a red toast only — the session was gone, so
      // every retry of Claim / Submit / a verdict failed the same way and the
      // buttons "did nothing". Same treatment a poll's 401 already gets. The
      // mutation's own onError still runs (and toasts), which is fine: the page
      // is about to navigate.
      onError: (error: unknown) => {
        if ((error as { status?: number })?.status === 401) redirectToLogin()
      },
    }),
    queryCache: new QueryCache({
      onError: (error: unknown) => {
        if ((error as { status?: number })?.status === 401) {
          redirectToLogin()
          return
        }
        const requestId = (error as { requestId?: string })?.requestId
        if (requestId) {
          const message = error instanceof Error ? error.message : 'Request failed'
          shadcnToast({
            description: <ErrorToastBody message={message} requestId={requestId} />,
            variant: 'destructive',
          })
        }
      },
    }),
  })
  return client
}

const queryClient = makeQueryClient()

/**
 * Client-side route gate. Roles come from the nav table rather than a second
 * hand-maintained list, so a link and its route can't drift apart — the old
 * shell gated only the links, so typing /audit as a technician rendered the
 * whole page and failed later at the API layer.
 *
 * `navPath` is the nav entry to inherit from: a sub-route like /collections/:id
 * passes '/collections'. This is a UX guard, not a security boundary; the
 * gateway still enforces permissions.
 */
function RequireRole({ navPath, children }: { navPath: string; children: ReactNode }) {
  const { me } = useAuth()
  if (!me) return null
  const item = NAV.find((n) => n.to === navPath)
  const allowed: Role[] = item?.roles ?? []
  if (me.role === 'admin' || allowed.includes(me.role)) return <>{children}</>
  return (
    <PageContainer>
      <PageHeader
        title="No access"
        description="You don't have access to this page."
        actions={
          <Button asChild variant="outline">
            <Link to={getDefaultLanding(me.role)}>Go to my start page</Link>
          </Button>
        }
      />
    </PageContainer>
  )
}

function DefaultRedirect() {
  const { me } = useAuth()
  const navigate = useNavigate()
  useEffect(() => {
    if (me) navigate(getDefaultLanding(me.role), { replace: true })
  }, [me, navigate])
  return null
}

function AnimatedRoutes() {
  const location = useLocation()
  const reduceMotion = useReducedMotion()
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
        animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
        exit={reduceMotion ? undefined : { opacity: 0, y: -4 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      >
        {/* One Suspense around the whole switch, inside the transition wrapper:
            the fallback replaces the page body only, so the shell and nav don't
            flash on a chunk fetch. */}
        <Suspense fallback={<LoadingSpinner label="Loading…" />}>
        <Routes location={location}>
          <Route path="/" element={<RequireRole navPath="/"><HomePage /></RequireRole>} />
          <Route path="/work" element={<RequireRole navPath="/work"><WorkPage /></RequireRole>} />
          <Route path="/activity" element={<RequireRole navPath="/activity"><ActivityPage /></RequireRole>} />
          <Route path="/dashboard" element={<RequireRole navPath="/dashboard"><DashboardPage /></RequireRole>} />
          {/* The per-user drill-down inherits `/dashboard`'s roles (admin +
              maintainer) rather than adding a nav entry of its own: it is reached
              by clicking a name in the work tracker, and a sidebar link to "one
              person's annotations" would have no person in it. */}
          <Route
            path="/dashboard/users/:userId"
            element={<RequireRole navPath="/dashboard"><UserAnnotationsPage /></RequireRole>}
          />
          <Route path="/workflows" element={<RequireRole navPath="/workflows"><WorkflowsPage /></RequireRole>} />
          <Route path="/collections" element={<RequireRole navPath="/collections"><CollectionsPage /></RequireRole>} />
          <Route
            path="/collections/:id"
            element={<RequireRole navPath="/collections"><CollectionDetailPage /></RequireRole>}
          />
          <Route path="/data" element={<RequireRole navPath="/data"><DataPage /></RequireRole>} />
          <Route path="/upload" element={<RequireRole navPath="/upload"><AdminUploadPage /></RequireRole>} />
          <Route path="/audit" element={<RequireRole navPath="/audit"><Audit /></RequireRole>} />
          <Route path="/settings" element={<RequireRole navPath="/settings"><SettingsPage /></RequireRole>} />

          {/* Redirects only — every one of these paths was a real route once,
              so bookmarks and external links have to keep landing somewhere. */}
          <Route path="/queue" element={<Navigate to="/work" replace />} />
          <Route path="/review" element={<Navigate to="/work" replace />} />
          <Route path="/my-work" element={<Navigate to="/activity" replace />} />
          <Route path="/export" element={<Navigate to="/data" replace />} />
          <Route path="/studies" element={<Navigate to="/collections" replace />} />
          <Route path="/manage" element={<Navigate to="/collections" replace />} />

          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  )
}

function AppRoutes() {
  const { loading } = useAuth()
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <LoadingSpinner label="Loading your session…" />
      </div>
    )
  }
  return (
    <AppShell>
      <AnimatedRoutes />
    </AppShell>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
